import clsx from "clsx";
import React from "react";
import { connect } from "react-redux";
import SwipeableViews from "react-swipeable-views";

import {
  characterSelectors,
  CharacterStatusSlug,
  FormatUtils,
} from "@dndbeyond/character-rules-engine/es";
import CharacterSheetSvg from "@dndbeyond/fontawesome-cache/svgs/light/address-card.svg";

import { Link } from "~/components/Link";
import { PremadeCharacterEditStatus } from "~/components/PremadeCharacterEditStatus";
import { Tooltip } from "~/components/Tooltip";

import { appEnvSelectors } from "../../../Shared/selectors";
import { MobileMessengerUtils } from "../../../Shared/utils";
import { navigationConfig } from "../../config";
import { builderEnvSelectors, builderSelectors } from "../../selectors";
import { BuilderAppState } from "../../typings";
import HelpTextManager from "../HelpTextManager";
import styles from "./styles.module.css";

interface Props {
  characterId: number | null;
  builderMethod: string | null;
  sections: Array<any>;
  firstAvailableSectionRoutes: any;
  isCharacterSheetReady: boolean;
  characterSheetUrl: string;
  activeSectionIdx: number;
  isMobile: boolean;
  characterStatus: CharacterStatusSlug | null;
  isReadonly: boolean;
}
class NavigationSections extends React.PureComponent<Props> {
  handleSheetShowClick = () => {
    const { characterId } = this.props;

    if (characterId !== null) {
      MobileMessengerUtils.sendMessage(
        MobileMessengerUtils.createShowCharacterSheetMessage(characterId)
      );
    }
  };

  renderCharacterSheetLink = (): React.ReactNode => {
    const { isCharacterSheetReady, characterSheetUrl } = this.props;

    //trading out a Router Link for a div if not ready for accessibility
    const Component = isCharacterSheetReady ? Link : "div";

    return (
      <div
        className={clsx([
          "builder-sections-sheet",
          !isCharacterSheetReady && "builder-sections-sheet-disabled",
        ])}
      >
        <Component
          className={clsx([
            styles.characterSheetIcon,
            !isCharacterSheetReady && styles.disabled,
          ])}
          href={isCharacterSheetReady ? characterSheetUrl : undefined}
          onClick={this.handleSheetShowClick}
          userouter={isCharacterSheetReady ? true : undefined}
          data-tooltip-place="bottom"
          data-tooltip-id={"character-sheet-tooltip"}
          data-tooltip-content={
            isCharacterSheetReady
              ? "Go to Character Sheet"
              : "Character is incomplete"
          }
        >
          <CharacterSheetSvg
            className={clsx([
              styles.characterSheetIcon,
              !isCharacterSheetReady && styles.disabled,
            ])}
          />
        </Component>
        <Tooltip id="character-sheet-tooltip" />
      </div>
    );
  };

  renderNonMobileUi = (): React.ReactNode => {
    const { characterId, sections, firstAvailableSectionRoutes } = this.props;

    return (
      <div className="builder-sections builder-sections-large">
        <HelpTextManager />
        {this.renderCharacterSheetLink()}
        <div className="builder-sections-large-routes">
          {sections.map((section) => {
            const firstRoute = firstAvailableSectionRoutes[section.key];
            let clsNames: Array<string> = [
              "builder-sections-link",
              `builder-sections-${FormatUtils.slugify(section.key)}`,
            ];
            if (section.active) {
              clsNames.push("builder-sections-link-active");
            }
            return (
              <div className="builder-sections-view" key={section.key}>
                <Link
                  href={firstRoute.path.replace(":characterId", characterId)}
                  className={clsNames.join(" ")}
                  userouter
                >
                  {section.getLabel()}
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  renderMobileUi = (): React.ReactNode => {
    const {
      characterId,
      sections,
      firstAvailableSectionRoutes,
      activeSectionIdx,
    } = this.props;

    const scrollbarStyles: React.CSSProperties = {
      padding: "0 37.5%",
    };

    return (
      <div className="builder-sections builder-sections-small">
        <HelpTextManager />
        {this.renderCharacterSheetLink()}
        <SwipeableViews
          index={activeSectionIdx}
          style={scrollbarStyles}
          className="builder-sections-views"
          ignoreNativeScroll={true}
        >
          {sections.map((section) => {
            const firstRoute = firstAvailableSectionRoutes[section.key];
            let clsNames: Array<string> = [
              "builder-sections-link",
              `builder-sections-${FormatUtils.slugify(section.key)}`,
            ];
            if (section.active) {
              clsNames.push("builder-sections-link-active");
            }
            return (
              <div className="builder-sections-view" key={section.key}>
                <Link
                  href={firstRoute.path.replace(":characterId", characterId)}
                  className={clsNames.join(" ")}
                  userouter
                >
                  {section.getLabel()}
                </Link>
              </div>
            );
          })}
        </SwipeableViews>
      </div>
    );
  };

  render() {
    const { builderMethod, isMobile, characterStatus, isReadonly } = this.props;

    if (builderMethod === null) {
      return null;
    }

    return isMobile ? (
      <>
        {this.renderMobileUi()}
        <PremadeCharacterEditStatus
          characterStatus={characterStatus}
          isReadonly={isReadonly}
          isBuilderView
        />
      </>
    ) : (
      <>
        {this.renderNonMobileUi()}
        <PremadeCharacterEditStatus
          characterStatus={characterStatus}
          isReadonly={isReadonly}
          isBuilderView
        />
      </>
    );
  }
}

function mapStateToProps(state: BuilderAppState, ownProps) {
  const currentPath = ownProps.pathname;
  const sections = navigationConfig.getNavigationSections(
    builderSelectors.getBuilderMethod(state),
    navigationConfig.getCurrentRouteDef(currentPath)
  );

  let activeSectionIdx: number = 0;
  sections.forEach((section, idx) => {
    if (section.active) {
      activeSectionIdx = idx;
    }
  });

  return {
    sections,
    firstAvailableSectionRoutes:
      builderSelectors.getFirstAvailableSectionRoutes(state),
    isCharacterSheetReady: builderSelectors.checkIsCharacterSheetReady(state),
    characterSheetUrl: builderEnvSelectors.getCharacterSheetUrl(state),
    builderMethod: builderSelectors.getBuilderMethod(state),
    activeSectionIdx,
    isMobile: appEnvSelectors.getIsMobile(state),
    characterStatus: characterSelectors.getStatusSlug(state),
    isReadonly: appEnvSelectors.getIsReadonly(state),
  };
}

export default connect(mapStateToProps)(NavigationSections);
